import { API, BACKEND_URL } from '@/config/api';
import { useCallback, useRef, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

/**
 * Custom hook for fetching and managing organism data
 * Handles both algae and zooplankton organisms from backend or localStorage (demo mode)
 */
export const useOrganismsFetching = ({
  locations,
  setOrganisms,
  setCurrentZooplanktonOrganisms,
  setSampleTypes,
  setOrganismsBySampleType,
  setZooplanktonGridData,
  setGridData,
  availableTemplates,
  selectedTemplate
}) => {
  // Track if this is the initial fetch to avoid showing toast on first load
  const fetchCountRef = useRef(0);
  // Store the full unfiltered lists as STATE so effects can react to changes
  const [allOrganisms, setAllOrganisms] = useState([]);
  const [allZooplankton, setAllZooplankton] = useState([]);
  
  const fetchOrganisms = useCallback(async () => {
    fetchCountRef.current += 1;
    console.trace('Call stack:');
    
    try {
      let algaeStructure;
      let zooplanktonData = [];
      
      // Default structures as fallback
      const defaultAlgae = {
        'Kiselalger': [
          { species: 'Skeletonema', depth: '3' },
          { species: 'Chaetoceros', depth: '3' },
          { species: 'Pseudo-Nitzschia', depth: '3' },
          { species: 'Div', depth: '3' }
        ],
        'Svepeflagellater': [
          { species: 'Chryso/Prymn', depth: '5' },
          { species: 'Phaeocystis', depth: '5' }
        ],
        'Nåleflagellater': [
          { species: 'Pseudochatonella', depth: '3' }
        ],
        'Kiselflagellater': [
          { species: 'Dictyocha/Vicicitus', depth: '5' }
        ],
        'Fureflagellater': [
          { species: 'Alexandrium', depth: '5' }
        ]
      };
      
      const defaultZooplanktonHierarchical = {
        'Håvtrekk': [
          { species: 'Mikromåneter' },
          { species: 'Kolonnmaneter' },
          { species: 'Pigghudlarver' },
          { species: 'Foraminifera' },
          { species: 'Hydroider' },
          { species: 'Radiolaria' },
          { species: 'Larver skjell' }
        ],
        'Visuelt': [
          { species: 'Manet' },
          { species: 'Ribbemanet' }
        ]
      };
      
      // Try to fetch from API
      try {
        const [algaeRes, zooplanktonRes] = await Promise.all([
          axios.get(`${API}/organisms/algae-structure`, { withCredentials: true }),
          axios.get(`${API}/organisms/zooplankton-structure`, { withCredentials: true })
        ]);
        
        algaeStructure = algaeRes.data;
        const zooplanktonStructure = zooplanktonRes.data;
        
        // Flatten zooplankton hierarchy to array
        zooplanktonData = [];
        Object.entries(zooplanktonStructure).forEach(([group, groupData]) => {
          const speciesList = Array.isArray(groupData) ? groupData : (groupData?.species || []);
          speciesList.forEach(sp => {
            zooplanktonData.push({
              sampleType: group,
              organism: sp.species,
              default_value: sp.default_value || '<5'
            });
          });
        });
        
      } catch (error) {
        console.error('Error fetching organisms from API, using defaults:', error);
        algaeStructure = defaultAlgae;
        
        // Flatten default zooplankton hierarchy to array
        zooplanktonData = [];
        Object.entries(defaultZooplanktonHierarchical).forEach(([group, species]) => {
          species.forEach(sp => {
            zooplanktonData.push({
              sampleType: group,
              organism: sp.species
            });
          });
        });
      }
      
      // Transform the hierarchical structure to flat array with SUM rows
      const flatOrganisms = [];
      
      // Custom sorting: specific order as requested
      const desiredOrder = [
        'Kiselalger',
        'Svepeflagellater',
        'Nåleflagellater',
        'Kiselflagellater',
        'Fureflagellater'
      ];
      
      const groups = Object.keys(algaeStructure).sort((a, b) => {
        const indexA = desiredOrder.indexOf(a);
        const indexB = desiredOrder.indexOf(b);
        
        // If both are in desired order, sort by their position
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }
        
        // If only one is in desired order, it comes first
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        
        // If neither is in desired order, sort alphabetically
        return a.localeCompare(b);
      });
      
      
      groups.forEach(group => {
        const groupData = algaeStructure[group];
        // Handle both formats: array or { species: [...] }
        const speciesInGroup = Array.isArray(groupData) ? groupData : (groupData?.species || []);
        
        // Add each species in the group
        speciesInGroup.forEach(sp => {
          flatOrganisms.push({
            group: group,
            species: sp.species,
            depth: sp.depth ? parseInt(sp.depth) : '',
            isSpecies: true,
            // Include threshold values for color coding
            threshold_green: sp.threshold_green,
            threshold_yellow: sp.threshold_yellow,
            threshold_orange: sp.threshold_orange,
            threshold_red: sp.threshold_red,
            // Include biovolume classes for distribution
            biovolume_classes: sp.biovolume_classes || []
          });
        });
        
        // Add SUM row ONLY for Kiselalger, including group_thresholds for auto-coloring
        if (group === 'Kiselalger') {
          const groupThresholds = groupData?.group_thresholds || {};
          flatOrganisms.push({
            group: group,
            species: `${group.toUpperCase()} SUM`,
            depth: '',
            isSum: true,
            threshold_yellow: groupThresholds.threshold_yellow || null,
            threshold_orange: groupThresholds.threshold_orange || null,
            threshold_red: groupThresholds.threshold_red || null
          });
        }
      });
      
      // Store full unfiltered lists for re-filtering when template changes
      setAllOrganisms(flatOrganisms);
      
      // Transform zooplankton to format expected by component
      const allTransformedZooplankton = zooplanktonData.map(z => ({
        sampleType: z.sampleType,
        organism: z.organism,
        isSpecies: true,
        default_value: z.default_value || '<5'
      }));
      setAllZooplankton(allTransformedZooplankton);
      
      // Template filtering is handled by the useEffect below (reacts to allOrganisms/allZooplankton/template changes)
      
      // Update sampleTypes and organismsBySampleType
      const uniqueSampleTypes = [...new Set(zooplanktonData.map(z => z.sampleType))];
      setSampleTypes(uniqueSampleTypes);
      
      const byType = {};
      zooplanktonData.forEach(z => {
        if (!byType[z.sampleType]) {
          byType[z.sampleType] = [];
        }
        byType[z.sampleType].push(z.organism);
      });
      setOrganismsBySampleType(byType);
      
      // Update zooplanktonGridData: preserve existing data, add new organisms
      setZooplanktonGridData(prevZooplanktonData => {
        const newZooplanktonData = { ...prevZooplanktonData }; // Start with existing data
        
        // Add any new zooplankton that don't exist yet
        allTransformedZooplankton.forEach(zoo => {
          const key = `${zoo.sampleType}_${zoo.organism}`;
          if (!newZooplanktonData[key]) {
            newZooplanktonData[key] = {};
            locations.forEach(loc => {
              newZooplanktonData[key][loc] = '';
            });
          }
        });
        
        return newZooplanktonData;
      });
      
      // Update gridData: preserve existing data, add new organisms
      setGridData(prevGridData => {
        const newGridData = { ...prevGridData }; // Start with existing data
        
        // Add any new organisms that don't exist yet
        flatOrganisms.forEach(org => {
          const key = `${org.group}_${org.species}`;
          if (!newGridData[key]) {
            newGridData[key] = {};
            locations.forEach(loc => {
              newGridData[key][loc] = '';
            });
          }
        });
        
        return newGridData;
      });
      
      
      // Show success message when called manually (not on initial load)
      if (fetchCountRef.current > 1) {
        toast.success('Organismeliste oppdatert fra innstillinger!');
      }
    } catch (error) {
      console.error('❌ Feil ved lasting av organismer:', error);
      // Keep the hardcoded fallback that's already in state
      toast.error('Kunne ikke laste organismer fra serveren. Bruker standard data.');
    }
  }, [
    locations,
    setOrganisms,
    setCurrentZooplanktonOrganisms,
    setSampleTypes,
    setOrganismsBySampleType,
    setZooplanktonGridData,
    setGridData
  ]);

  // Apply template filter whenever allOrganisms, allZooplankton, template, or availableTemplates change
  // IMPORTANT: Preserve organisms/zooplankton that have data in gridData/zooplanktonGridData even if
  // they're not in the current template (custom organisms added by user, or template changed after save).
  useEffect(() => {
    if (!allOrganisms.length) return;
    
    // If a template is selected but templates haven't loaded yet, wait — don't show unfiltered list
    if (selectedTemplate && (!availableTemplates || availableTemplates.length === 0)) return;
    
    const templateConfig = availableTemplates?.find(t => t.id === selectedTemplate);
    const templateOrgs = templateConfig?.config?.organisms;
    
    // Filter algae organisms
    let finalOrganisms = allOrganisms;
    if (templateOrgs && templateOrgs.length > 0) {
      const templateKeys = new Set(templateOrgs.map(o => `${o.group}_${o.species}`));
      const templateGroups = new Set(templateOrgs.map(o => o.group));
      finalOrganisms = allOrganisms.filter(o => {
        if (o.isSum) return templateGroups.has(o.group);
        return templateKeys.has(`${o.group}_${o.species}`);
      });
    }
    setOrganisms(finalOrganisms);
    
    // Filter zooplankton, but preserve custom ones with saved data
    const templateZoo = templateConfig?.config?.zooplankton;
    let finalZoo = allZooplankton;
    if (templateZoo && templateZoo.length > 0) {
      const templateZooKeys = new Set(templateZoo.map(z => `${z.sampleType}_${z.organism}`));
      const filtered = allZooplankton.filter(z => templateZooKeys.has(`${z.sampleType}_${z.organism}`));
      if (filtered.length === 0) {
        finalZoo = templateZoo.map(z => ({ sampleType: z.sampleType, organism: z.organism, isSpecies: true, default_value: z.default_value || '<5' }));
      } else {
        finalZoo = filtered;
      }
    }
    
    // Preserve any zooplankton that is currently displayed (from loaded bulk/draft) with actual data
    // This prevents custom zooplankton from disappearing when template filter runs after load
    setCurrentZooplanktonOrganisms(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return finalZoo;
      const finalKeys = new Set(finalZoo.map(z => `${z.sampleType}_${z.organism}`));
      // Keep any existing entries not in template but that look custom (isCustom) or already loaded
      const extras = prev.filter(p => {
        const key = `${p.sampleType}_${p.organism}`;
        return !finalKeys.has(key) && (p.isCustom || p.__fromSavedData);
      });
      if (extras.length === 0) return finalZoo;
      return [...finalZoo, ...extras];
    });
  }, [allOrganisms, allZooplankton, availableTemplates, selectedTemplate, setOrganisms, setCurrentZooplanktonOrganisms]);

  return { fetchOrganisms };
};
